import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  TextField,
  Typography,
  Button,
  Grid,
  MenuItem
} from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import { EmojiFlags, Backup } from '@mui/icons-material'
import Draft from '@components/Editor/Draft'
import { EditorState, ContentState } from 'draft-js'
import htmlToDraft from 'html-to-draftjs'
import {
  setDealName,
  setDealDesc,
  setDealWebsiteUrl,
  setDealCrypto,
  setLeadInvestor,
  setPresailAmount,
  setMinsaleAmount,
  setMaxsaleAmount,
  setStartBonus,
  setEndBonus,
  setDealLogo,
  setOfferPeriod,
  setMinStakeAmount,
  setMaxStakeAmount
} from '@modules/redux/actions'
import { TOKENS_INFO } from '@utils/tokenHelper'
import IMAGE_BONUS_DETAIL from '@images/bonus_detail.png'
import "@pages/DealInfo/DealInfo.scss"

const AddDealDialog = (props) => {
  const {
    open,
    closeModal,
    eventHandler,
    textFieldError,
    decimal,
    dealInfo,
    isNew,
    name,
    desc,
    websiteUrl,
    leadInvestor,
    crypto,
    presailAmount,
    minsaleAmount,
    maxsaleAmount,
    minStakeAmount,
    maxStakeAmount,
    startBonus,
    endBonus,
    logo,
    offerPeriod,
    setDealName,
    setDealDesc,
    setDealWebsiteUrl,
    setLeadInvestor,
    setDealCrypto,
    setPresailAmount,
    setMinsaleAmount,
    setMaxsaleAmount,
    setMinStakeAmount,
    setMaxStakeAmount,
    setStartBonus,
    setEndBonus,
    setDealLogo,
    setOfferPeriod
  } = props

  const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
  const [previewImg, setPreviewImg] = useState('')
  const isLeadStaked = isNew ? false : dealInfo?.isLeadStaked

  useEffect(() => {
    if (logo !== '') {
      let reader = new FileReader();
      reader.readAsDataURL(logo);
      reader.onloadend = function (e) {
        setPreviewImg(reader.result)
      };
    }
  }, [logo])

  useEffect(() => {
    if(!isNew) {
      setDealName(dealInfo.name)
      // Set Description
      const blocksFromHtml = htmlToDraft(dealInfo.desc);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      setDealDesc(EditorState.createWithContent(contentState))
  
      const _leadInvestor = dealInfo.leadInvestor === ZERO_ADDRESS ? '' : dealInfo.leadInvestor
      setLeadInvestor(_leadInvestor)
      setPresailAmount(dealInfo.presaleAmount / (10 ** decimal))
      setMinsaleAmount(dealInfo.minsaleAmount / (10 ** decimal))
      setMaxsaleAmount(dealInfo.maxsaleAmount / (10 ** decimal))
      setStartBonus(dealInfo.startBonus)
      setEndBonus(dealInfo.endBonus)
      setMinStakeAmount(dealInfo.minStakeAmount / (10 ** decimal))
      setMaxStakeAmount(dealInfo.maxStakeAmount / (10 ** decimal))
    }
  }, [isNew])

  const selectFileHandler = (e) => {
    const file = e.target.files[0];
    setDealLogo(file);
  }

  return (
    <Backdrop
      open={open}
      className="modal-filter"
    >
      <Dialog
        open={open}
        onClose={closeModal}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{ textAlign: "center", textTransform: "uppercase" }}
        >
          {isNew ? "Post a new Presail deal" : "Update " + dealInfo?.name}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography
              variant="div"
            >
              Post a Presail deal to get investor validation and build fundraising momentum. Read this wiki for help and advice.
            </Typography>
          </DialogContentText>
          <Box
            className="dialog-form-panel add-deal-modal"
            sx={{ mt: 2 }}
          >
            <Box
              sx={{ display: "flex", mb: 1, alignItems: "center" }}
            >
              <Box
                sx={{ position: "relative", overflow: "hidden" }}
              >
                {isNew ? (
                  <>
                    {previewImg !== '' ? (
                      <img src={previewImg} alt="logo img" className="preview-image" />
                    ) : (
                      <Box className="icon-upload">
                        <Backup
                          sx={{ color: "#787878" }}
                        />
                      </Box>
                    )}
                    <input
                      type="file"
                      className="input-upload"
                      onChange={(e) => selectFileHandler(e)}
                    />
                  </>
                ) : (
                  <img src={dealInfo?.logo} alt="logo img" className="preview-image" />
                )}
              </Box>
              <Box className="text-default" sx={{ ml: 2 }}>{isNew ? "Upload" : "Deal"} logo</Box>
            </Box>
            <TextField
              required
              disabled={!isNew}
              variant="outlined"
              autoFocus
              margin="dense"
              label="Deal Name"
              type="text"
              value={name}
              fullWidth
              size="small"
              helperText={
                textFieldError?.name ? 'Incorrect entry' : ''
              }
              error={textFieldError?.name ? true : false}
              onChange={(e) => {
                setDealName(e.target.value);
              }}
            />
            <TextField
              variant="outlined"
              margin="dense"
              label="Website URL"
              type="url"
              value={websiteUrl}
              fullWidth
              size="small"
              helperText={
                textFieldError?.websiteUrl ? 'Incorrect entry' : ''
              }
              error={textFieldError?.websiteUrl ? true : false}
              onChange={(e) => {
                setDealWebsiteUrl(e.target.value);
              }}
            />
            <Draft
              value={desc}
              onChange={setDealDesc}
              placeholder="Deal Description"
            />
            <DialogContentText
              sx={{ mt: 2 }}
            >
              Deal Requirement
            </DialogContentText>
            <Grid container rowSpacing={0} columnSpacing={{ xs: 1 }}>
              <Grid item sm={12}>
                <TextField
                  disabled={isLeadStaked}
                  variant="outlined"
                  margin="dense"
                  label="Lead Investor"
                  type="text"
                  value={leadInvestor}
                  fullWidth
                  size="small"
                  placeholder="Wallet Address"
                  helperText={
                    isLeadStaked ? 'The lead investor has already staked.' : textFieldError?.leadInvestor ? 'Invalid address' : ''
                  }
                  error={textFieldError?.leadInvestor ? true : false}
                  onChange={(e) => {
                    setLeadInvestor(e.target.value);
                  }}
                />
              </Grid>
            </Grid>
            <TextField
              required
              disabled={!isNew}
              variant="outlined"
              margin="dense"
              label="Deal Crypto"
              select
              fullWidth
              value={crypto}
              helperText={
                textFieldError?.crypto ? 'Choose crypto' : ''
              }
              error={textFieldError?.crypto ? true : false}
              onChange={(e) => {
                setDealCrypto(e.target.value)
              }}
            >
              {TOKENS_INFO.map((token, index) => (
                <MenuItem
                  key={index}
                  value={token.address}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", mr: 2 }}
                    >
                      <img src={token.logo} style={{ width: "24px", height: "24px" }} alt="crypto icon" />
                    </Box>
                    <Box>{token.name}</Box>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <Grid container rowSpacing={0} columnSpacing={{ xs: 1 }}>
              <Grid item sm={6}>
                <TextField
                  required
                  variant="outlined"
                  margin="dense"
                  label="Presail Amount"
                  type="number"
                  value={presailAmount === 0 ? '' : presailAmount}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText={
                    textFieldError?.presailAmount ? 'Incorrect entry' : ''
                  }
                  error={textFieldError?.presailAmount ? true : false}
                  onChange={(e) => {
                    setPresailAmount(e.target.value);
                  }}
                />
              </Grid>
              <Grid item sm={6}>
                <TextField
                  required
                  disabled={!isNew}
                  variant="outlined"
                  margin="dense"
                  label="Offer Period"
                  type="number"
                  value={offerPeriod === 0 ? '' : offerPeriod}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="start">hours</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText={
                    textFieldError?.offerPeriod ? 'Incorrect entry' : ''
                  }
                  error={textFieldError?.offerPeriod ? true : false}
                  onChange={(e) => {
                    setOfferPeriod(e.target.value);
                  }}
                />
              </Grid>
            </Grid>
            <Grid container rowSpacing={0} columnSpacing={{ xs: 1 }}>
              <Grid item sm={6}>
                <TextField
                  required
                  variant="outlined"
                  margin="dense"
                  label="Minimum Sale Amount"
                  type="number"
                  value={minsaleAmount === 0 ? '' : minsaleAmount}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText={
                    textFieldError?.minsaleAmount ? 'Incorrect entry' : ''
                  }
                  error={textFieldError?.minsaleAmount ? true : false}
                  onChange={(e) => {
                    setMinsaleAmount(e.target.value);
                  }}
                />
              </Grid>
              <Grid item sm={6}>
                <TextField
                  required
                  variant="outlined"
                  margin="dense"
                  label="Maximum Sale Amount"
                  type="number"
                  value={maxsaleAmount === 0 ? '' : maxsaleAmount}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText={
                    textFieldError?.maxsaleAmount ? 'Incorrect entry' : ''
                  }
                  error={textFieldError?.maxsaleAmount ? true : false}
                  onChange={(e) => {
                    setMaxsaleAmount(e.target.value);
                  }}
                />
              </Grid>
            </Grid>
            <Grid container rowSpacing={0} columnSpacing={{ xs: 1 }}>
              <Grid item sm={6}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  label="Start Bonus"
                  type="number"
                  value={startBonus === 0 ? '' : startBonus}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="start">%</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText={
                    textFieldError?.startBonus ? 'Incorrect entry' : ''
                  }
                  error={textFieldError?.startBonus ? true : false}
                  onChange={(e) => {
                    setStartBonus(e.target.value);
                  }}
                />
              </Grid>
              <Grid item sm={6}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  label="End Bonus"
                  type="number"
                  value={endBonus === 0 ? '' : endBonus}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="start">%</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText={
                    textFieldError?.endBonus ? 'Incorrect entry' : ''
                  }
                  error={textFieldError?.endBonus ? true : false}
                  onChange={(e) => {
                    setEndBonus(e.target.value);
                  }}
                />
              </Grid>
            </Grid>

            <DialogContentText
              sx={{ mt: 2 }}
            >
              Stake Limit
            </DialogContentText>

            <Grid container rowSpacing={0} columnSpacing={{ xs: 1 }}>
              <Grid item sm={6}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  label="Minimum Stake Amount"
                  type="number"
                  value={minStakeAmount === 0 ? '' : minStakeAmount}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText={
                    textFieldError?.minStakeAmount ? 'Incorrect entry' : ''
                  }
                  error={textFieldError?.minStakeAmount ? true : false}
                  onChange={(e) => {
                    setMinStakeAmount(e.target.value);
                  }}
                />
              </Grid>
              <Grid item sm={6}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  label="Maximum Stake Amount"
                  type="number"
                  value={maxStakeAmount === 0 ? '' : maxStakeAmount}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText={
                    textFieldError?.maxStakeAmount ? 'Incorrect entry' : ''
                  }
                  error={textFieldError?.maxStakeAmount ? true : false}
                  onChange={(e) => {
                    setMaxStakeAmount(e.target.value);
                  }}
                />
              </Grid>
            </Grid>
            <Box
              sx={{ textAlign: "center", mt: 3 }}
            >
              <img
                src={IMAGE_BONUS_DETAIL} alt="Deal Bonus"
                style={{ width: "80%" }}
              />
            </Box>
            <Button
              variant="outlined"
              fullWidth
              className="btn-info btn-lg"
              sx={{ mt: 2 }}
              onClick={eventHandler}
              startIcon={<EmojiFlags />}
            >
              {isNew ? "Post a Deal" : "Update a Deal"}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ mb: 1 }}>
          <Button
            color="primary"
            fullWidth
            onClick={closeModal}
            className="btn-lg"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Backdrop>
  )
}

const mapStateToProps = ({ deal }) => ({
  name: deal.name,
  desc: deal.desc,
  websiteUrl: deal.websiteUrl,
  crypto: deal.crypto,
  leadInvestor: deal.leadInvestor,
  presailAmount: deal.presailAmount,
  minsaleAmount: deal.minsaleAmount,
  maxsaleAmount: deal.maxsaleAmount,
  minStakeAmount: deal.minStakeAmount,
  maxStakeAmount: deal.maxStakeAmount,
  startBonus: deal.startBonus,
  endBonus: deal.endBonus,
  logo: deal.logo,
  offerPeriod: deal.offerPeriod
});

const mapToDispatchProps = (dispatch) => ({
  setDealName: (name) => dispatch(setDealName(name)),
  setDealDesc: (desc) => dispatch(setDealDesc(desc)),
  setDealWebsiteUrl: (url) => dispatch(setDealWebsiteUrl(url)),
  setLeadInvestor: (addr) => dispatch(setLeadInvestor(addr)),
  setDealCrypto: (crypto) => dispatch(setDealCrypto(crypto)),
  setPresailAmount: (amount) => dispatch(setPresailAmount(amount)),
  setMinsaleAmount: (amount) => dispatch(setMinsaleAmount(amount)),
  setMaxsaleAmount: (amount) => dispatch(setMaxsaleAmount(amount)),
  setMinStakeAmount: (amount) => dispatch(setMinStakeAmount(amount)),
  setMaxStakeAmount: (amount) => dispatch(setMaxStakeAmount(amount)),
  setStartBonus: (bonus) => dispatch(setStartBonus(bonus)),
  setEndBonus: (bonus) => dispatch(setEndBonus(bonus)),
  setDealLogo: (file) => dispatch(setDealLogo(file)),
  setOfferPeriod: (period) => dispatch(setOfferPeriod(period)),
});

export default connect(mapStateToProps, mapToDispatchProps)(AddDealDialog);